import * as Page from '../../components/content/page-header';
import { Content, RightActions } from '../../components/layout/content';
import * as Button from '../../components/button/button';
import { FieldValues, useForm } from 'react-hook-form';
import { Label, TextInputLarge } from '../../components/forms/input';
import React, { useEffect, useRef } from 'react';
import { Checkbox2 } from '../../components/forms/checkbox';
import { Select2 } from '../../components/forms/select';
import { useQuery } from '../../hooks/use-query';
import { Pageable, usePagination } from '../../hooks/use-pagination';
import { Extent } from './types/extent';
import { AppSettings } from './types/app-settings';
import { Language } from '../internationalization/types/language';
import { usePutMutation } from '../../hooks/use-mutations';
import { Configuration } from '../config/types/configurations';

export const General = (): JSX.Element => {
  const formRef = useRef<HTMLFormElement>(null);
  const { register, handleSubmit, getValues, setValue } = useForm();
  const appSettings = useQuery<AppSettings>({ path: '/appsettings' });
  const languages = useQuery<Language[]>({ path: '/languages/all' });
  const appSettingsMutation = usePutMutation({ path: '/appsettings' });
  const extents = usePagination<Pageable<Extent>>('/extent', { size: 9999 });
  const configurations = usePagination<Pageable<Configuration>>('/configurations', { size: 9999 });

  useEffect(() => {
    if (appSettings.isSuccess) {
      setValues(appSettings.data);
    }
  }, [appSettings.isSuccess]);

  const onSubmit = (data: FieldValues): void => {
    appSettingsMutation.mutate(data);
  };

  const setValues = (item: AppSettings): void => {
    setTimeout(() => {
      for (const key in item) {
        setValue(key, item[key as keyof AppSettings]);
      }
    }, 500);
  };

  return <>
    <RightActions>
      <Button.Primary type="submit" form="general-settings-form" disabled={appSettings.isLoading || appSettingsMutation.isLoading}>
        Update
      </Button.Primary>
      <Button.Tertiary disabled={appSettings.isLoading || appSettingsMutation.isLoading}>
        Cancel
      </Button.Tertiary>
    </RightActions>
    <Content>
      <Page.Header>
        <div className="mb-2">
          <Page.Title>General</Page.Title>
        </div>
        <Page.SubTitle>
          DisasterAWARE configuration settings.
        </Page.SubTitle>
      </Page.Header>
      <div>
        <form id="general-settings-form" onSubmit={handleSubmit(onSubmit)} ref={formRef}>
          <div className="mb-4">
            <Label name="appId">Application Id</Label>
            <TextInputLarge {...register('appId')} />
          </div>
          <div className="mb-4">
            <Label name="defaultLang">Default language</Label>
            <Select2 { ...register('defaultLang')}>
              <option value="">Select Language</option>
              {languages.data?.map(language => <option key={language.id} value={language.id}>{language.name}</option>)}
            </Select2>
          </div>
          <div className="mb-4">
            <Label name="configurationId">Default Configuration</Label>
            <Select2 { ...register('configurationId')}>
              <option value="">Select Configuration</option>
              {configurations.data?.content?.map(item => {
                return <option key={item.id} value={item.id}>{item.name}</option>;
              })}
            </Select2>
          </div>
          <div className="mb-4">
            <Label name="baseUrl">Base URL</Label>
            <TextInputLarge {...register('baseUrl')} />
          </div>
          <div className="mb-4">
            <Label name="appVersion">Version Info</Label>
            <TextInputLarge {...register('appVersion')} />
          </div>
          <div className="mb-4">
            <Label name="exportAppVersion">Export App Version</Label>
            <TextInputLarge {...register('exportAppVersion')} />
          </div>
          <div className="mb-4">
            <Checkbox2 {...register('startupAuthentication')} { ...{ defaultChecked: getValues().startupAuthentication, label: 'Startup Authentication' }} />
          </div>
          <div className="mb-4">
            <Checkbox2 {...register('alwaysUseSwatchesInLegend')} { ...{ defaultChecked: getValues().alwaysUseSwatchesInLegend, label: 'Always Use Swatches In Legend' }} />
          </div>
          <div className="mb-4">
            <Label name="defaultExtentId">Default Extent</Label>
            <Select2 { ...register('defaultExtentId')}>
              <option value="">Select Default Extent</option>
              {extents.data?.content?.map(extent => <option key={extent.id} value={extent.id}>{extent.slug}[{extent.maxX}, {extent.maxY}, {extent.minX}, {extent.minY}]</option>)}
            </Select2>
          </div>
          <div className="mb-4">
            <Label name="maxExtentId">Max Extent</Label>
            <Select2 { ...register('maxExtentId')}>
              <option value="">Select Max Extent</option>
              {extents.data?.content?.map(extent => <option key={extent.id} value={extent.id}>{extent.slug}[{extent.maxX}, {extent.maxY}, {extent.minX}, {extent.minY}]</option>)}
            </Select2>
          </div>
        </form>
      </div>
    </Content>
  </>;
};
